import { useEffect, useRef, useState } from "react";
import { motion, animate, useInView } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { MaskLines, Reveal } from "./Motion";
import { scrollToId } from "@/lib/scroll";
import { useT, useLang } from "@/i18n";

const REASONS = [
  ["Esperienza, non improvvisazione", "Dal 1905 lavoriamo il legno ogni giorno. Più di un secolo di mestiere tramandato di generazione in generazione: sappiamo cosa funziona perché l'abbiamo già fatto, e rifatto."],
  ["Conoscenza profonda della materia", "Conosciamo il legno dal tronco alla struttura finita: lamellare BSH, X-LAM, massiccio, perline, fino a bricchetti e trucioli. Nulla va sprecato, ogni scelta tecnica è consapevole."],
  ["Amore e passione per il legno", "Per noi non è solo un prodotto. È il lavoro di una famiglia che ama questo materiale e mette in ogni trave la stessa cura che metterebbe nella propria casa."],
  ["Un solo interlocutore", "Progetto, produzione, carpenteria metallica, trasporto e posa in opera: un solo referente, un solo contratto, una sola responsabilità."],
  ["Qualità certificata", "Legno lamellare con marcatura CE e controllo della produzione verificato da un ente terzo notificato (certificato 0407-CPR-1459)."],
  ["Strutture che altri non fanno", "Travi fino a 44 m di lunghezza, elementi curvi e speciali, cupole e grandi coperture: affrontiamo i progetti che richiedono vero saper fare."],
];

const STATS = [[1905, "Anno di fondazione", false], [9000, "m³ di legno lavorati ogni anno", true], [944, "Strutture realizzate", true]];

const SEP = { it: ".", en: ",", fr: "\u202F" };
const group = (n, lang) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, SEP[lang] ?? ".");

const CountUp = ({ value, run }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.15 });
  const { lang } = useLang();
  const [n, setN] = useState(value);
  useEffect(() => {
    if (!run || !inView) return undefined;
    const controls = animate(0, value, { duration: 2.2, ease: [0.22, 1, 0.36, 1], onUpdate: (v) => setN(Math.round(v)) });
    return () => controls.stop();
  }, [run, inView, value]);
  return <span ref={ref}>{run ? group(n, lang) : value}</span>;
};

const Stat = ({ s, i, t }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
    className="border-l-2 border-[#121110] pl-5"
    data-testid={`why-stat-${i}`}
  >
    <p className="font-display text-5xl font-bold tabular-nums tracking-tight sm:text-6xl lg:text-7xl"><CountUp value={s[0]} run={s[2]} /></p>
    <p className="mt-2 font-mono text-xs uppercase tracking-[0.2em] text-[#121110]/80">{t(`why.s${i}.l`, s[1])}</p>
  </motion.div>
);

const Reason = ({ r, i, t }) => (
  <motion.li
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.8, delay: (i % 3) * 0.12, ease: [0.22, 1, 0.36, 1] }}
    className="group border-t border-[#121110]/30 pt-6"
    data-testid={`why-reason-${i}`}
  >
    <span className="font-mono text-xs tracking-[0.25em] text-[#121110]/70">{String(i + 1).padStart(2, "0")}</span>
    <h3 className="mt-4 font-display text-2xl font-bold leading-tight tracking-tight transition-transform duration-500 group-hover:translate-x-2 sm:text-3xl">{t(`why.r${i}.t`, r[0])}</h3>
    <p className="mt-4 text-base leading-relaxed text-[#121110]/85">{t(`why.r${i}.d`, r[1])}</p>
  </motion.li>
);

export const WhyEmma = () => {
  const t = useT();
  return (
    <section id="perche-emma" className="grain relative overflow-hidden bg-[#C26D38] px-5 py-28 text-[#121110] sm:px-8 lg:py-40" data-testid="why-section">
      <div className="mx-auto max-w-[1500px]">
        <div className="flex items-center gap-4 font-mono text-xs uppercase tracking-[0.25em] text-[#121110]/80">
          <span>(✓)</span><span className="h-px w-10 bg-[#121110]/40" /><span>{t("why.label", "Perché sceglierci")}</span>
        </div>
        <div className="mt-12 grid gap-10 lg:grid-cols-12 lg:items-end">
          <MaskLines
            className="font-display text-4xl font-bold leading-[1.02] tracking-tight sm:text-5xl lg:col-span-8 lg:text-[4.6rem]"
            lines={[t("why.h1", "Perché Emma Industrie"), <span key="b">{t("why.h2a", "e non")} <em className="font-serif font-normal text-[#F7F5F0]">{t("why.h2em", "qualcun altro.")}</em></span>]}
          />
          <Reveal delay={0.2} className="max-w-md text-base leading-relaxed text-[#121110]/85 lg:col-span-4 sm:text-lg">
            {t("why.intro", "Chiunque può vendere legno. Pochi lo conoscono davvero. Ecco cosa ci distingue.")}
          </Reveal>
        </div>
        <div className="mt-16 grid gap-10 sm:grid-cols-3">
          {STATS.map((s, i) => <Stat key={s[1]} s={s} i={i} t={t} />)}
        </div>
        <ul className="mt-20 grid gap-x-12 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
          {REASONS.map((r, i) => <Reason key={r[0]} r={r} i={i} t={t} />)}
        </ul>
        <Reveal delay={0.1} className="mt-20">
          <button onClick={() => scrollToId("#contatti")} className="inline-flex items-center gap-3 rounded-full bg-[#121110] px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-[#F7F5F0] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#2A2622]" data-testid="why-cta">
            {t("why.cta", "Mettici alla prova: chiedi un preventivo")} <ArrowRight className="h-4 w-4" />
          </button>
        </Reveal>
      </div>
    </section>
  );
};
